import { useEffect, useRef, useState } from 'react';
import { Modal } from '../components/Modal';
import { Smartphone } from 'lucide-react';

interface InstallPrompt extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Chamado pela pagina antes de carregar o perfil para capturar o evento a tempo.
export function useAppInstallation() {
    const promptRef = useRef<InstallPrompt | null>(null);
    const [installed, setInstalled] = useState(false);
    const [busy, setBusy] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);

    useEffect(() => {
        const standalone = window.matchMedia('(display-mode: standalone)');
        const update = () => setInstalled(standalone.matches || !!(navigator as Navigator & { standalone?: boolean }).standalone);
        const capture = (event: Event) => {
            event.preventDefault();
            promptRef.current = event as InstallPrompt;
        };
        const onInstalled = () => {
            promptRef.current = null;
            setInstalled(true);
            setShowInstructions(false);
        };
        update();
        standalone.addEventListener('change', update);
        window.addEventListener('beforeinstallprompt', capture);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            standalone.removeEventListener('change', update);
            window.removeEventListener('beforeinstallprompt', capture);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    const install = async () => {
        if (busy) return;
        const deferred = promptRef.current;
        if (!deferred) {
            setShowInstructions(true);
            return;
        }
        promptRef.current = null;
        setBusy(true);
        try {
            await deferred.prompt();
            // Aceitar o aviso nao garante que a instalacao terminou; aguardar appinstalled.
            await deferred.userChoice;
        } catch {
            setShowInstructions(true);
        } finally {
            setBusy(false);
        }
    };

    return { installed, busy, showInstructions, install, close: () => setShowInstructions(false) };
}

export function InstallApp({ installation }: { installation: ReturnType<typeof useAppInstallation> }) {
    if (installation.installed) return null;
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const android = /Android/i.test(navigator.userAgent);
    const steps = ios ? [
        'Abra esta página no Safari. Se estiver no Instagram ou WhatsApp, use o menu para abrir no navegador.',
        'Toque em Compartilhar (quadrado com seta para cima).',
        'Selecione Adicionar à Tela de Início. Se necessário, role a lista de opções.',
        'Se aparecer Abrir como App da Web, deixe ativado e toque em Adicionar.',
    ] : android ? [
        'Abra esta página no Chrome. Se estiver no Instagram ou WhatsApp, use o menu para abrir no navegador.',
        'Toque no menu de três pontos (⋮).',
        'Escolha Instalar app ou Adicionar à tela inicial, conforme a opção disponível.',
        'Confirme em Instalar ou Adicionar.',
    ] : [
        'Abra esta página no Chrome ou Microsoft Edge.',
        'Procure o ícone de instalação na barra de endereços ou a opção Instalar no menu do navegador.',
        'Confirme a instalação. Se a opção não aparecer, continue acessando pelo navegador.',
    ];

    return (
        <>
            <section aria-labelledby="install-app-title" className="flex items-center gap-3.5 border-b border-base-content/[0.06] py-5 sm:gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-base-100 text-base-content/70 ring-1 ring-base-content/[0.06]" aria-hidden>
                    <Smartphone aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                    <h2 id="install-app-title" className="text-[0.9375rem] font-semibold leading-snug">Tenha o app na tela inicial</h2>
                    <p className="mt-0.5 hidden text-sm text-base-content/60 sm:block">Acesse seus agendamentos com mais facilidade pelo ícone no seu aparelho.</p>
                </div>
                <button type="button" className="btn h-10 min-h-10 shrink-0 rounded-full border-0 bg-base-100 px-5 text-sm ring-1 ring-base-content/10 hover:bg-base-100/70" onClick={installation.install} disabled={installation.busy}>
                    {installation.busy ? 'Aguarde…' : 'Instalar'}
                </button>
            </section>
            {installation.showInstructions && (
                <Modal title="Instalar app" onClose={installation.close} desktopClassName="sm:!max-w-lg">
                    <p className="mb-5 text-sm text-base-content/65">{ios ? 'No iPhone e iPad, adicione o app pelo menu de compartilhamento.' : 'Se o aviso de instalação não apareceu, siga estes passos:'}</p>
                    <ol className="list-decimal space-y-4 pl-5 text-sm leading-relaxed">
                        {steps.map((step) => <li key={step} className="pl-1">{step}</li>)}
                    </ol>
                    <p className="mt-5 text-xs leading-relaxed text-base-content/55">As opções variam conforme o navegador. Se o app já estiver instalado, procure o ícone na tela inicial ou na lista de aplicativos.</p>
                    <button type="button" className="btn btn-primary mt-6 w-full" onClick={installation.close}>Entendi</button>
                </Modal>
            )}
        </>
    );
}
